// 案 B: ボトムシート分析型 (iOS Apple ヘルス風)
// - 上部 chip で指標切替 (横スクロール)
// - 中央に主要 1 指標を大表示 + 24ヶ月推移
// - 下部に診療科別ランキング (ボトムシート風カード、 引上げ展開)

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta, TOP9_METRICS, ml } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { StatPill, sentimentFromRatio, sentimentFromDelta } from "../components/StatBadge";
import { Sparkline } from "../components/Sparkline";
import { fmtByUnitMain, fmtByUnit, fmtRatio } from "../../lib/format";
import type { KpiResponse, RankingResponse, Direction } from "@shared/api";

export function PrototypeB() {
  const { data: meta } = useMeta();
  const [metric, setMetric] = useState<string>("入院収入");
  const [sheetExpanded, setSheetExpanded] = useState(false);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;
  const asOfMonth = meta.dateMax;
  const periodFrom = monthShift(asOfMonth, -11);
  const metricDef = meta.metrics.find((x) => x["集計区分"] === metric);
  const unit = metricDef?.unit ?? "";
  const direction = (metricDef?.direction ?? "neutral") as Direction;

  return (
    <MobileShell title="病院全体" prototypeId="B" prototypeName="ボトムシート" asOfMonth={asOfMonth}>
      <MetricChips selected={metric} onSelect={setMetric} />
      <MainKpiBlock
        metric={metric}
        unit={unit}
        direction={direction}
        asOfMonth={asOfMonth}
        periodFrom={periodFrom}
      />
      <DeptRankingSheet
        metric={metric}
        unit={unit}
        expanded={sheetExpanded}
        onToggle={() => setSheetExpanded((v) => !v)}
        asOfMonth={asOfMonth}
        periodFrom={periodFrom}
      />
    </MobileShell>
  );
}

// 上部 chip: 横スクロール
function MetricChips({ selected, onSelect }: {
  selected: string; onSelect: (m: string) => void;
}) {
  return (
    <div className="-mx-3 px-3 overflow-x-auto">
      <div className="flex gap-1.5 pb-1 min-w-max">
        {TOP9_METRICS.map((m) => (
          <button
            key={m}
            onClick={() => onSelect(m)}
            className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
              selected === m
                ? "bg-slate-900 text-white font-semibold"
                : "bg-white border border-slate-200 text-slate-600 active:bg-slate-50"
            }`}
          >
            {ml(m)}
          </button>
        ))}
      </div>
    </div>
  );
}

// 中央メインカード
function MainKpiBlock({ metric, unit, direction, asOfMonth, periodFrom }: {
  metric: string; unit: string; direction: Direction; asOfMonth: string; periodFrom: string;
}) {
  const { data } = useQuery<KpiResponse>({
    queryKey: ["kpi-b", metric, asOfMonth],
    queryFn: () => api.kpi({
      granularity: "小", iryo: "合計", yotei: "合計", metric, asOfMonth, periodFrom,
    }),
    placeholderData: (prev) => prev,
  });
  const adj = (v: number | null | undefined): number | null => {
    if (v == null) return null;
    if (["GP", "GP単価"].includes(metric) && v === 0) return null;
    return v;
  };
  const current = adj(data?.current);
  const budget  = adj(data?.budget);
  const prevYear = adj(data?.prevYear);
  const prevMonth = adj(data?.prevMonth);
  const main = fmtByUnitMain(current, unit);
  const ratio = budget != null && current != null && budget !== 0 ? current / budget : null;
  const ratioSent = sentimentFromRatio(ratio, direction);
  const yoyDelta = current != null && prevYear != null && prevYear !== 0
    ? (current - prevYear) / Math.abs(prevYear) : null;
  const momDelta = current != null && prevMonth != null && prevMonth !== 0
    ? (current - prevMonth) / Math.abs(prevMonth) : null;
  const yoySent = sentimentFromDelta(yoyDelta, direction);
  const momSent = sentimentFromDelta(momDelta, direction);

  const trendValues = data?.trend.map((p) => p.actual) ?? [];

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
      <div className="text-xs text-slate-500">{ml(metric)} <span className="text-slate-400">({unit})</span></div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-4xl font-bold text-slate-900 tabular-nums tracking-tight">{main.value}</span>
        {main.unit && <span className="text-sm text-slate-500">{main.unit}</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <StatPill label="予算比" value={ratio != null ? fmtRatio(ratio) : "—"} sentiment={ratioSent} size="sm" />
        <StatPill label="前年比" value={yoyDelta != null ? `${yoyDelta >= 0 ? "+" : ""}${(yoyDelta * 100).toFixed(1)}%` : "—"} sentiment={yoySent} size="sm" />
        <StatPill label="前月比" value={momDelta != null ? `${momDelta >= 0 ? "+" : ""}${(momDelta * 100).toFixed(1)}%` : "—"} sentiment={momSent} size="sm" />
      </div>
      <div className="mt-4 text-[10px] text-slate-400">24ヶ月推移</div>
      <div className="mt-1 -mx-1">
        <Sparkline values={trendValues} width={320} height={64} showLast stroke="#0f172a" fill="rgba(15,23,42,0.06)" />
      </div>
      <div className="mt-2 grid grid-cols-3 text-center text-[10px] text-slate-500">
        <div>予算 <div className="font-semibold text-slate-700">{fmtByUnit(budget, unit)}</div></div>
        <div>前年 <div className="font-semibold text-slate-700">{fmtByUnit(prevYear, unit)}</div></div>
        <div>前月 <div className="font-semibold text-slate-700">{fmtByUnit(prevMonth, unit)}</div></div>
      </div>
    </section>
  );
}

// ボトムシート: 診療科別ランキング
function DeptRankingSheet({ metric, unit, expanded, onToggle, asOfMonth }: {
  metric: string; unit: string; expanded: boolean; onToggle: () => void; asOfMonth: string; periodFrom: string;
}) {
  const { data } = useQuery<RankingResponse>({
    queryKey: ["ranking-b", metric, asOfMonth],
    queryFn: () => api.ranking({
      iryo: "合計", yotei: "合計", metric,
      dateFrom: asOfMonth, dateTo: asOfMonth,
    }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => !r["診療科"].includes("合計") && !r["診療科"].includes("総合計"));
  }, [data]);

  const visible = expanded ? rows : rows.slice(0, 5);

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-slate-700">診療科別ランキング (実績 多い順)</h2>
        <span className="text-[10px] text-slate-400">{rows.length} 科</span>
      </div>
      <ol className="space-y-1.5">
        {visible.map((r, i) => (
          <li key={r["診療科"]} className="flex items-center gap-2 px-2 py-1.5 rounded bg-slate-50">
            <span className="text-[10px] font-mono text-slate-400 w-5 shrink-0">#{i + 1}</span>
            <span className="text-sm text-slate-800 flex-1 truncate">{r["診療科"]}</span>
            <span className="text-xs text-slate-700 font-semibold tabular-nums shrink-0">{fmtByUnit(r.total, unit)}</span>
          </li>
        ))}
      </ol>
      {rows.length > 5 && (
        <button
          onClick={onToggle}
          className="mt-2 w-full text-xs text-blue-600 active:text-blue-800 py-1.5 rounded hover:bg-slate-50"
        >
          {expanded ? "▲ 折り畳む" : `▼ 残り ${rows.length - 5} 科を表示`}
        </button>
      )}
    </section>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
